import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { AuthorizedUser, ConfigStorage, getFormErrorList, isObjectValidator, requiredValidator, UserStorage } from "@scisys-uim/uim-common";
import { AssetsUser, Firearm, FIREARM_ASSIGNABLE_PERSON_TYPE, FIREARM_TYPE, FirearmAssignablePerson, FirearmAssignment, FIREARM_TYPE_MODERATOR } from "assets-model";
import { FirearmAssignablePersonService, FirearmAssignmentCancellationData, FirearmAssignmentService } from "assets-service";
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";


export interface FirearmAssignmentCancellationDialogData {
  comments?: string;
  firearmAssignment?: FirearmAssignment;
}

export interface FirearmAssignmentCancellationDialogConfigData {
  firearmAssignment?: FirearmAssignment;
}

@Component({
  selector: 'app-firearm-assignment-cancellation-dialog',
  templateUrl: './firearm-assignment-cancellation-dialog.component.html',
  styleUrls: ['./firearm-assignment-cancellation-dialog.component.scss']
})
export class FirearmAssignmentCancellationDialogComponent implements OnInit {

  protected systemConfig: any;

  public form: FormGroup;

  public item: FirearmAssignmentCancellationDialogData = {};

  protected userDetails: AuthorizedUser;

  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  get firearmAssetTypeName(): string {
    const firearm = this.data?.firearmAssignment?.firearm;
    if (firearm != null) {
      if (firearm?.firearmType == FIREARM_TYPE_MODERATOR) {
        return 'Moderator';
      } else {
        return 'Firearm';
      }
    }
    return "Firearm Asset";
  }

  private _onInitCompleteSubject: Subject<boolean> = new BehaviorSubject<boolean>(null);
  $onInitComplete: Observable<boolean> = this._onInitCompleteSubject.asObservable().pipe(filter(o => o != null));
  private onInitSubscription: Subscription = null;

  constructor(
    private dialogRef: MatDialogRef<FirearmAssignmentCancellationDialogComponent, FirearmAssignmentCancellationDialogData>,
    @Inject(MAT_DIALOG_DATA) public data: FirearmAssignmentCancellationDialogConfigData,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private firearmAssignablePersonService: FirearmAssignablePersonService,
    private firearmAssigmentService: FirearmAssignmentService,
    protected configService: ConfigStorage,
    protected userStorage: UserStorage) {

    dialogRef.disableClose = true;

    this.data = this.data || {
    };

    this.form = this.fb.group({
      comments: [null, [requiredValidator]],
    });

    this.item.firearmAssignment = this.data.firearmAssignment;

    this.setDefaultDisabledFields();
  }

  protected onInitAsyncComplete() { }

  ngAfterViewInit() {
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
  }

  getFormErrorList() {
    var result = [...getFormErrorList(this.form)];
    return result;
  }

  closeDialog() {
    this.dialogRef.close();
  }

  isCancelEnabled() {
    return !this.form.invalid;
  }

  cancelFirearmAssignment() {
    const serviceParam: FirearmAssignmentCancellationData = {
      firearmAssignmentId: this.item?.firearmAssignment?.firearmAssignmentId,
      reason: this.item?.comments
    };

    this.firearmAssigmentService.cancelAssignment(serviceParam).subscribe(firearmAssignment => {
      this.item.firearmAssignment = firearmAssignment;
      this.dialogRef.close(this.item);
    });
  }

  firearmDisplay(item: Firearm) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_TYPE.description(item);
  }

  get firearm() {
    return this.control('firearm');
  }

  personDisplay(item: FirearmAssignablePerson) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_ASSIGNABLE_PERSON_TYPE.description(item);
  }

  get person() {
    return this.control('person');
  }

  // Comments field
  commentsChanging(value: string, unsetDependents: boolean = true) { }

  commentsChanged(value: string, unsetDependents: boolean = true) {
  }

  get comments() {
    return this.control('comments');
  }

  // form controls general

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  // 'framework'

  ngOnInit() {
    this.onInitAsync();
  }


  protected onInitAsync() {
    const allObservables: Observable<any>[] = this.onInitAsyncObservables() || [];
    this.onInitSubscription = combineLatest(allObservables).subscribe(() => {
      this.onInitSubscription?.unsubscribe();
      this._onInitAsyncComplete();
    });
  }

  protected _onInitAsyncComplete() {
    this.onInitAsyncComplete();
    this.configService.itemChanges().subscribe((configData: any) => this.initConfig(configData));
    this._onInitCompleteSubject.next(true);
    this._onInitCompleteSubject.complete();
  }

  // These observables will be waited for, on page load, before the screen is ready to search.
  // These Observables are required by the framework - take care in overriding
  protected onInitAsyncObservables(): Observable<any>[] {
    return [this.onInitConfigObservable(),
    this.onInitUserDetails()];
  }
  protected onInitConfigObservable(): Observable<any> {
    return this.configService.resolve().pipe(tap((configData: any) => this.initConfig(configData)));
  }
  protected initConfig(config: any) {
    this.systemConfig = config || {};
  }
  protected onInitUserDetails(): Observable<AuthorizedUser> {
    return this.userStorage.nextNonNullItem().pipe(tap((userDetails: AuthorizedUser) => this.initUserDetails(userDetails)));
  }
  protected initUserDetails(userDetails: AuthorizedUser) {
    this.userDetails = userDetails;
  }


  disabledTooltip(element) {
    return element && (element.valid || !element.touched || !element.value);
  }
}